/**
 * Real-time Subscription Hooks for Workflow Events
 * 
 * Provides WebSocket-based real-time updates for Enhanced components
 * Integrates with GraphQL subscriptions for live workflow monitoring
 */

import { useSubscription } from '@apollo/client/react';
import {
  ERROR_OCCURRED,
  ESCALATION_TRIGGERED,
  INSTANCE_STATE_CHANGED,
  InstanceStateChangedInput,
  POLICY_EVALUATION_EVENT,
  PolicyEvaluationEventInput,
  SCHEDULE_EXECUTED,
  ScheduleExecutedInput,
  SIGNAL_RECEIVED,
  SYSTEM_HEALTH_UPDATE,
  TASK_ASSIGNED,
  TASK_STATUS_UPDATED,
  TaskAssignedInput,
  TASKS_FOR_USER_UPDATED,
  TaskStatusUpdatedInput,
  TENANT_ACTIVITY_STREAM,
  TenantActivityStreamInput,
  WORKFLOW_ACTIVITY_STREAM,
  WORKFLOW_EXECUTION_EVENT,
  WorkflowActivityStreamInput,
  WorkflowExecutionEventInput
} from '@ferp/graphql-client';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react';

// Status constants (matches generated GraphQL enums)
const Status = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

interface NotificationOptions {
  enabled?: boolean;
  showSuccess?: boolean;
  showErrors?: boolean;
  showWarnings?: boolean;
  showInfo?: boolean;
}

interface SubscriptionCallbacks<T = any> {
  onData?: (data: T) => void;
  onError?: (error: any) => void;
  onComplete?: () => void;
}

/**
 * Hook for instance state change notifications
 */
export function useInstanceStateChanges(
  input: InstanceStateChangedInput,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: true,
    showErrors: true,
    showWarnings: true,
    showInfo: true,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(INSTANCE_STATE_CHANGED, {
    variables: input,
    onData: (options) => {
      const eventData = options.data?.data?.instanceStateChanged;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      // Show notifications based on state changes
      if (eventData && notifyOptions.enabled) {
        const { instanceId, previousState, currentState, status } = eventData;

        if (status === Status.COMPLETED && notifyOptions.showSuccess) {
          notifications.show({
            title: 'Workflow Completed',
            message: `Instance ${instanceId} completed successfully`,
            color: 'green',
            icon: <IconCheck size={16} />
          });
        } else if (status === Status.FAILED && notifyOptions.showErrors) {
          notifications.show({
            title: 'Workflow Failed',
            message: `Instance ${instanceId} failed in state ${currentState}`,
            color: 'red',
            icon: <IconX size={16} />
          });
        } else if (currentState !== previousState && notifyOptions.showInfo) {
          notifications.show({
            title: 'State Changed',
            message: `Instance ${instanceId}: ${previousState} → ${currentState}`,
            color: 'blue',
            icon: <IconInfoCircle size={16} />
          });
        }
      }
    },
    onError: (error: any) => {
      if (callbacks?.onError) {
        callbacks.onError(error);
      }
      if (notifyOptions.enabled && notifyOptions.showErrors) {
        notifications.show({
          title: 'Subscription Error',
          message: 'Failed to receive instance state updates',
          color: 'red',
          icon: <IconX size={16} />
        });
      }
    },
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.instanceStateChanged,
    loading,
    error
  };
}

/**
 * Hook for task assignment notifications
 */
export function useTaskAssignments(
  input: TaskAssignedInput,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: true,
    showErrors: true,
    showWarnings: false,
    showInfo: true,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(TASK_ASSIGNED, {
    variables: input,
    onData: (options) => {
      const eventData = options.data?.data?.taskAssigned;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      if (eventData && notifyOptions.enabled && notifyOptions.showInfo) {
        const { taskId, title, assignee, priority } = eventData;
        notifications.show({
          title: 'New Task Assigned',
          message: `Task "${title}" assigned to ${assignee}`,
          color: priority === 'HIGH' ? 'orange' : 'blue',
          icon: <IconInfoCircle size={16} />
        });
      }
    },
    onError: (error: any) => {
      if (callbacks?.onError) {
        callbacks.onError(error);
      }
      if (notifyOptions.enabled && notifyOptions.showErrors) {
        notifications.show({
          title: 'Subscription Error',
          message: 'Failed to receive task assignment updates',
          color: 'red',
          icon: <IconX size={16} />
        });
      }
    },
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.taskAssigned,
    loading,
    error
  };
}

/**
 * Hook for task status updates
 */
export function useTaskStatusUpdates(
  input: TaskStatusUpdatedInput,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: true,
    showErrors: true,
    showWarnings: true,
    showInfo: false,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(TASK_STATUS_UPDATED, {
    variables: input,
    onData: (options) => {
      const eventData = options.data?.data?.taskStatusUpdated;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      if (eventData && notifyOptions.enabled) {
        const { taskId, previousStatus, currentStatus, approvalDecision } = eventData;

        if (currentStatus === Status.COMPLETED && notifyOptions.showSuccess) {
          notifications.show({
            title: 'Task Completed',
            message: `Task ${taskId} has been completed`,
            color: 'green',
            icon: <IconCheck size={16} />
          });
        } else if (currentStatus === Status.FAILED && notifyOptions.showErrors) {
          notifications.show({
            title: 'Task Failed',
            message: `Task ${taskId} failed`,
            color: 'red',
            icon: <IconX size={16} />
          });
        } else if (approvalDecision === 'REJECTED' && notifyOptions.showWarnings) {
          notifications.show({
            title: 'Task Rejected',
            message: `Task ${taskId} was rejected`,
            color: 'yellow',
            icon: <IconAlertTriangle size={16} />
          });
        }
      }
    },
    onError: (error: any) => {
      if (callbacks?.onError) {
        callbacks.onError(error);
      }
      if (notifyOptions.enabled && notifyOptions.showErrors) {
        notifications.show({
          title: 'Subscription Error',
          message: 'Failed to receive task status updates',
          color: 'red',
          icon: <IconX size={16} />
        });
      }
    },
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.taskStatusUpdated,
    loading,
    error
  };
}

/**
 * Hook for user task updates
 */
export function useUserTaskUpdates(
  userId: string,
  tenant: string,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const { data, loading, error } = useSubscription<Record<string, any>>(TASKS_FOR_USER_UPDATED, {
    variables: { tenant, userId },
    onData: (options) => {
      const eventData = options.data?.data?.tasksForUserUpdated;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.tasksForUserUpdated,
    loading,
    error
  };
}

/**
 * Hook for workflow execution events
 */
export function useWorkflowExecutionEvents(
  input: WorkflowExecutionEventInput,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: false,
    showErrors: true,
    showWarnings: true,
    showInfo: false,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(WORKFLOW_EXECUTION_EVENT, {
    variables: input,
    onData: (options) => {
      const eventData = options.data?.data?.workflowExecutionEvent;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      if (eventData && notifyOptions.enabled) {
        const { eventType, executionResult, errorDetails } = eventData;

        if (executionResult === 'ERROR' && errorDetails && notifyOptions.showErrors) {
          notifications.show({
            title: 'Workflow Error',
            message: `Execution error: ${errorDetails}`,
            color: 'red',
            icon: <IconX size={16} />
          });
        }
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.workflowExecutionEvent,
    loading,
    error
  };
}

/**
 * Hook for signal received events
 */
export function useSignalReceived(
  instanceId: string,
  tenant: string,
  callbacks?: SubscriptionCallbacks
) {
  const { data, loading, error } = useSubscription<Record<string, any>>(SIGNAL_RECEIVED, {
    variables: { tenant, instanceId },
    onData: (options) => {
      const eventData = options.data?.data?.signalReceived;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.signalReceived,
    loading,
    error
  };
}

/**
 * Hook for schedule execution events
 */
export function useScheduleExecutions(
  input: ScheduleExecutedInput,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: false,
    showErrors: true,
    showWarnings: false,
    showInfo: false,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(SCHEDULE_EXECUTED, {
    variables: input,
    onData: (options) => {
      const eventData = options.data?.data?.scheduleExecuted;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      if (eventData && notifyOptions.enabled && notifyOptions.showErrors) {
        const { scheduleName, status, errorDetails } = eventData;

        if (status === Status.FAILED && errorDetails) {
          notifications.show({
            title: 'Schedule Failed',
            message: `Schedule "${scheduleName}" failed: ${errorDetails}`,
            color: 'red',
            icon: <IconX size={16} />
          });
        }
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.scheduleExecuted,
    loading,
    error
  };
}

/**
 * Hook for policy evaluation events
 */
export function usePolicyEvaluations(
  input: PolicyEvaluationEventInput,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: false,
    showErrors: true,
    showWarnings: true,
    showInfo: false,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(POLICY_EVALUATION_EVENT, {
    variables: input,
    onData: (options) => {
      const eventData = options.data?.data?.policyEvaluationEvent;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      if (eventData && notifyOptions.enabled) {
        const { policyName, evaluationResult, errorDetails, enforcementMode } = eventData;

        if (evaluationResult === 'DENY' && enforcementMode === 'ENFORCING' && notifyOptions.showWarnings) {
          notifications.show({
            title: 'Policy Violation',
            message: `Policy "${policyName}" denied operation`,
            color: 'yellow',
            icon: <IconAlertTriangle size={16} />
          });
        } else if (errorDetails && notifyOptions.showErrors) {
          notifications.show({
            title: 'Policy Error',
            message: `Policy "${policyName}" evaluation failed`,
            color: 'red',
            icon: <IconX size={16} />
          });
        }
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.policyEvaluationEvent,
    loading,
    error
  };
}

/**
 * Hook for escalation events
 */
export function useEscalationEvents(
  tenant: string,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: false,
    showErrors: false,
    showWarnings: true,
    showInfo: true,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(ESCALATION_TRIGGERED, {
    variables: { tenant },
    onData: (options) => {
      const eventData = options.data?.data?.escalationTriggered;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      if (eventData && notifyOptions.enabled) {
        const { escalationName, escalationLevel, triggerReason } = eventData;

        if (escalationLevel === 'CRITICAL' && notifyOptions.showWarnings) {
          notifications.show({
            title: 'Critical Escalation',
            message: `${escalationName}: ${triggerReason}`,
            color: 'red',
            icon: <IconAlertTriangle size={16} />
          });
        } else if (notifyOptions.showInfo) {
          notifications.show({
            title: 'Escalation Triggered',
            message: `${escalationName} (${escalationLevel})`,
            color: 'orange',
            icon: <IconInfoCircle size={16} />
          });
        }
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.escalationTriggered,
    loading,
    error
  };
}

/**
 * Hook for system health updates
 */
export function useSystemHealth(
  tenant: string,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: false,
    showErrors: true,
    showWarnings: true,
    showInfo: false,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(SYSTEM_HEALTH_UPDATE, {
    variables: { tenant },
    onData: (options) => {
      const eventData = options.data?.data?.systemHealthUpdate;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      if (eventData && notifyOptions.enabled) {
        const { overallHealth, alerts } = eventData;

        if (overallHealth === 'CRITICAL' && notifyOptions.showErrors) {
          notifications.show({
            title: 'System Health Critical',
            message: 'System health is in critical state',
            color: 'red',
            icon: <IconX size={16} />
          });
        }

        // Show critical alerts
        alerts?.forEach((alert: any) => {
          if (alert.level === 'CRITICAL' && notifyOptions.showErrors) {
            notifications.show({
              title: `Critical Alert - ${alert.component}`,
              message: alert.message,
              color: 'red',
              icon: <IconX size={16} />
            });
          } else if (alert.level === 'WARNING' && notifyOptions.showWarnings) {
            notifications.show({
              title: `Warning - ${alert.component}`,
              message: alert.message,
              color: 'yellow',
              icon: <IconAlertTriangle size={16} />
            });
          }
        });
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.systemHealthUpdate,
    loading,
    error
  };
}

/**
 * Hook for error events
 */
export function useErrorEvents(
  tenant: string,
  callbacks?: SubscriptionCallbacks,
  notificationOptions?: NotificationOptions
) {
  const notifyOptions = {
    enabled: true,
    showSuccess: false,
    showErrors: true,
    showWarnings: true,
    showInfo: false,
    ...notificationOptions
  };

  const { data, loading, error } = useSubscription<Record<string, any>>(ERROR_OCCURRED, {
    variables: { tenant },
    onData: (options) => {
      const eventData = options.data?.data?.errorOccurred;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }

      if (eventData && notifyOptions.enabled) {
        const { errorType, severity, message, component } = eventData;

        if (severity === 'CRITICAL' && notifyOptions.showErrors) {
          notifications.show({
            title: `Critical Error - ${component}`,
            message: `${errorType}: ${message}`,
            color: 'red',
            icon: <IconX size={16} />
          });
        } else if (severity === 'HIGH' && notifyOptions.showWarnings) {
          notifications.show({
            title: `Error - ${component}`,
            message: `${errorType}: ${message}`,
            color: 'orange',
            icon: <IconAlertTriangle size={16} />
          });
        }
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.errorOccurred,
    loading,
    error
  };
}

/**
 * Hook for workflow activity streams
 */
export function useWorkflowActivityStream(
  input: WorkflowActivityStreamInput,
  callbacks?: SubscriptionCallbacks
) {
  const { data, loading, error } = useSubscription<Record<string, any>>(WORKFLOW_ACTIVITY_STREAM, {
    variables: input,
    onData: (options) => {
      const eventData = options.data?.data?.workflowActivityStream;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.workflowActivityStream,
    loading,
    error
  };
}

/**
 * Hook for tenant activity streams
 */
export function useTenantActivityStream(
  input: TenantActivityStreamInput,
  callbacks?: SubscriptionCallbacks
) {
  const { data, loading, error } = useSubscription<Record<string, any>>(TENANT_ACTIVITY_STREAM, {
    variables: input,
    onData: (options) => {
      const eventData = options.data?.data?.tenantActivityStream;
      if (eventData && callbacks?.onData) {
        callbacks.onData(eventData);
      }
    },
    onError: callbacks?.onError,
    onComplete: callbacks?.onComplete
  });

  return {
    data: data?.tenantActivityStream,
    loading,
    error
  };
}

/**
 * Composite hook for comprehensive workflow monitoring
 */
export function useWorkflowMonitoring(
  tenant: string,
  instanceId?: string,
  userId?: string,
  options?: {
    enableNotifications?: boolean;
    notificationOptions?: NotificationOptions;
    callbacks?: {
      onInstanceStateChange?: (data: any) => void;
      onTaskAssigned?: (data: any) => void;
      onTaskStatusUpdate?: (data: any) => void;
      onWorkflowEvent?: (data: any) => void;
      onError?: (error: any) => void;
    };
  }
) {
  const { enableNotifications = true, notificationOptions, callbacks } = options || {};

  // Instance state changes
  const instanceState = useInstanceStateChanges(
    { tenant, instanceId },
    { onData: callbacks?.onInstanceStateChange, onError: callbacks?.onError },
    enableNotifications ? notificationOptions : { enabled: false }
  );

  // Task assignments for user
  const taskAssignments = useTaskAssignments(
    { tenant, assignee: userId },
    { onData: callbacks?.onTaskAssigned, onError: callbacks?.onError },
    enableNotifications ? notificationOptions : { enabled: false }
  );

  // Task status updates
  const taskStatusUpdates = useTaskStatusUpdates(
    { tenant },
    { onData: callbacks?.onTaskStatusUpdate, onError: callbacks?.onError },
    enableNotifications ? notificationOptions : { enabled: false }
  );

  // Workflow execution events
  const workflowEvents = useWorkflowExecutionEvents(
    { tenant, instanceId },
    { onData: callbacks?.onWorkflowEvent, onError: callbacks?.onError },
    enableNotifications ? notificationOptions : { enabled: false }
  );

  // System health
  const systemHealth = useSystemHealth(
    tenant,
    { onError: callbacks?.onError },
    enableNotifications ? notificationOptions : { enabled: false }
  );

  // Error events
  const errorEvents = useErrorEvents(
    tenant,
    { onError: callbacks?.onError },
    enableNotifications ? notificationOptions : { enabled: false }
  );

  return {
    instanceState,
    taskAssignments,
    taskStatusUpdates,
    workflowEvents,
    systemHealth,
    errorEvents,
    isLoading: instanceState.loading || taskAssignments.loading || taskStatusUpdates.loading,
    hasErrors: !!(instanceState.error || taskAssignments.error || taskStatusUpdates.error)
  };
}

export default useWorkflowMonitoring;